import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Users, ZoomIn, ZoomOut, RotateCcw, Network } from 'lucide-react';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  referralCode: string;
  nickname: string;
  walletAddress: string;
  referredBy?: string;
  vttnBalance: number;
  totalReferrals: number;
  totalVolume: number;
  createdAt: string;
  tier: string;
  isActive: boolean;
}

interface Link {
  source: string | Node;
  target: string | Node;
  value: number;
}

interface ReferralNodeMapProps {
  nodes: Node[];
  links: Link[];
  selectedUserId?: string;
  onNodeClick?: (node: Node) => void;
  width?: number;
  height?: number;
}

const ReferralNodeMap: React.FC<ReferralNodeMapProps> = ({
  nodes,
  links,
  selectedUserId,
  onNodeClick,
  width = 1200,
  height = 800
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(1);
  const [transform, setTransform] = useState<d3.ZoomTransform | null>(null);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // 컨테이너 그룹 생성 (줌/패닝용)
    const container = svg.append('g').attr('class', 'container');

    // 시뮬레이션 설정
    const simulation = d3.forceSimulation(nodes as any)
      .force('link', d3.forceLink(links as any).id((d: any) => d.id).distance(150))
      .force('charge', d3.forceManyBody().strength(-500))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius((d: any) => {
        const size = Math.max(20, Math.min(50, Math.sqrt(d.totalReferrals + 1) * 4));
        return size + 5;
      }));

    // 줌/패닝 설정 (마우스 휠 줌, 드래그 패닝)
    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 5])
      .on('zoom', (event) => {
        setTransform(event.transform);
        container.attr('transform', event.transform.toString());
      });

    svg.call(zoomBehavior as any);
    
    // 시뮬레이션 완료 후 초기 줌 설정
    simulation.on('end', () => {
      setTimeout(() => {
        const bounds = container.node()?.getBBox();
        if (bounds && bounds.width > 0 && bounds.height > 0) {
          const fullWidth = bounds.width;
          const fullHeight = bounds.height;
          const scale = Math.min(width / fullWidth, height / fullHeight, 1) * 0.9;
          const translateX = (width - fullWidth * scale) / 2 - bounds.x * scale;
          const translateY = (height - fullHeight * scale) / 2 - bounds.y * scale;
          svg.transition().duration(750).call(
            zoomBehavior.transform as any,
            d3.zoomIdentity.translate(translateX, translateY).scale(scale)
          );
        }
      }, 100);
    });

    // 링크 그리기
    const link = container.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', (d: any) => Math.sqrt(d.value) / 10);

    // 노드 그리기
    const node = container.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .call(d3.drag<SVGGElement, Node>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended) as any);

    // 노드 배경 (라운드 박스)
    const nodeBox = node.append('rect')
      .attr('width', (d) => {
        const textWidth = (d.nickname || d.referralCode).length * 8 + 20;
        return Math.max(80, textWidth);
      })
      .attr('height', 50)
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('x', (d) => {
        const textWidth = (d.nickname || d.referralCode).length * 8 + 20;
        return -Math.max(80, textWidth) / 2;
      })
      .attr('y', -25)
      .attr('fill', (d) => {
        if (d.id === selectedUserId) return '#fee2e2'; // 선택된 사용자: 연한 빨강
        if (d.totalReferrals > 100) return '#fef3c7'; // 대량 추천인: 연한 주황
        if (d.totalReferrals > 50) return '#dbeafe'; // 중간 추천인: 연한 파랑
        if (d.totalReferrals > 10) return '#d1fae5'; // 소량 추천인: 연한 초록
        return '#f3f4f6'; // 일반: 연한 회색
      })
      .attr('stroke', (d) => {
        if (d.id === selectedUserId) return '#ef4444';
        if (d.totalReferrals > 100) return '#f59e0b';
        if (d.totalReferrals > 50) return '#3b82f6';
        if (d.totalReferrals > 10) return '#10b981';
        return '#9ca3af';
      })
      .attr('stroke-width', 2)
      .style('cursor', 'pointer');

    // 노드 아이콘 (원형)
    const nodeCircle = node.append('circle')
      .attr('r', (d) => {
        const size = Math.max(18, Math.min(25, Math.sqrt(d.totalReferrals + 1) * 2.5));
        return size;
      })
      .attr('cx', 0)
      .attr('cy', -10)
      .attr('fill', (d) => {
        if (d.id === selectedUserId) return '#ef4444'; // 선택된 사용자: 빨강
        if (d.totalReferrals > 100) return '#f59e0b'; // 대량 추천인: 주황
        if (d.totalReferrals > 50) return '#3b82f6'; // 중간 추천인: 파랑
        if (d.totalReferrals > 10) return '#10b981'; // 소량 추천인: 초록
        return '#6b7280'; // 일반: 회색
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 3)
      .style('cursor', 'pointer');

    // 노드 라벨 (닉네임)
    const label = node.append('text')
      .text((d) => d.nickname || d.referralCode)
      .attr('font-size', '11px')
      .attr('font-weight', '600')
      .attr('dx', 0)
      .attr('dy', 8)
      .attr('text-anchor', 'middle')
      .attr('fill', '#1f2937')
      .style('pointer-events', 'none');

    // 노드 서브 라벨 (추천인 수)
    const subLabel = node.append('text')
      .text((d) => `추천: ${d.totalReferrals}명`)
      .attr('font-size', '9px')
      .attr('dx', 0)
      .attr('dy', 22)
      .attr('text-anchor', 'middle')
      .attr('fill', '#6b7280')
      .style('pointer-events', 'none');

    // 노드 클릭 이벤트
    nodeBox.on('click', (event, d) => {
      event.stopPropagation();
      if (onNodeClick) onNodeClick(d);
    });
    nodeCircle.on('click', (event, d) => {
      event.stopPropagation();
      if (onNodeClick) onNodeClick(d);
    });

    // 툴팁
    const tooltip = d3.select('body').append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('padding', '8px')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', 1000);

    node.on('mouseover', (event, d) => {
      tooltip.transition().duration(200).style('opacity', 1);
      tooltip.html(`
        <div><strong>${d.nickname}</strong></div>
        <div>코드: ${d.referralCode}</div>
        <div>추천인: ${d.totalReferrals}명</div>
        <div>거래량: ${(d.totalVolume / 1000).toFixed(0)}K VTTN</div>
        <div>잔액: ${d.vttnBalance.toFixed(0)} VTTN</div>
        <div>티어: ${d.tier}</div>
      `)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 10) + 'px');
    })
    .on('mousemove', (event) => {
      tooltip
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 10) + 'px');
    })
    .on('mouseout', () => {
      tooltip.transition().duration(200).style('opacity', 0);
    });

    // 시뮬레이션 업데이트
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    return () => {
      tooltip.remove();
      simulation.stop();
    };
  }, [nodes, links, selectedUserId, width, height, onNodeClick]);

  const handleZoomIn = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().call(d3.zoom<SVGSVGElement, unknown>().scaleBy as any, 1.5);
    }
  };

  const handleZoomOut = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().call(d3.zoom<SVGSVGElement, unknown>().scaleBy as any, 1 / 1.5);
    }
  };

  const handleReset = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().call(d3.zoom<SVGSVGElement, unknown>().transform as any, d3.zoomIdentity);
    }
  };

  return (
    <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* 컨트롤 버튼 */}
      <div className="absolute top-4 right-4 z-10 flex flex-col space-y-2">
        <button
          onClick={handleZoomIn}
          className="p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          title="확대"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          title="축소"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          title="초기화"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {/* 범례 */}
      <div className="absolute top-4 left-4 z-10 bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
        <div className="text-xs font-semibold text-gray-700 mb-2">범례</div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>선택된 사용자</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span>추천인 100명+</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>추천인 50-100명</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>추천인 10-50명</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
            <span>일반 사용자</span>
          </div>
        </div>
      </div>

      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="w-full h-full"
        style={{ cursor: 'grab' }}
        onMouseDown={() => {
          if (svgRef.current) {
            d3.select(svgRef.current).style('cursor', 'grabbing');
          }
        }}
        onMouseUp={() => {
          if (svgRef.current) {
            d3.select(svgRef.current).style('cursor', 'grab');
          }
        }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 10 3, 0 6" fill="#999" />
          </marker>
        </defs>
      </svg>
    </div>
  );
};

export default ReferralNodeMap;

